// https://wiki.yandex.com/homepage/builddocs/proektirovanie/builddocs-2.0/interfejjs/zhurnaly/ozhr-statusnaja-model/otobrazhenie-knopok-statusnojj-modeli/

import { useQuery } from "@tanstack/react-query";
import { Button } from "antd";
import { journalsQueries } from "@/entities/journal";
import {
  SaveIcon,
  SendCheckIcon,
  BackCheckIcon,
  DoneIcon,
  CloseIcon,
  ArrowSendIcon,
} from "@/shared/assets/svg/icons";
import { Can } from "@/shared/lib/casl";
import { useCoordinator } from "@/shared/lib/hooks";
import "./side-button-config.scss";

interface ISideButtonConfig {
  onOpen: () => void;
  setModalContentNew: (arg0: {
    okTest: string;
    cancelText: string;
    title: string;
    content: string;
    isEditTest: boolean;
  }) => void;
  setStatus: (arg0: string) => void;
}

const ModalContentMap = {
  SAVE: {
    title: "Подтвердите сохранение",
    content: "Вы уверены, что хотите сохранить изменения?",
    isEditTest: false,
    okTest: "Сохранить",
    cancelText: "Отмена",
  },
  SEND_APPROVAL: {
    title: "Отправка на проверку",
    content: "Вы уверены, что хотите отправить титульный лист на проверку?",
    isEditTest: false,
    okTest: "Отправить",
    cancelText: "Отмена",
  },
  REVOKE: {
    title: "Отозвать титульный лист",
    content: "Вы уверены, что хотите отозвать титульный лист?",
    isEditTest: true,
    okTest: "Отозвать",
    cancelText: "Отмена",
  },
  APPROVE: {
    title: "Подтвердить проверку",
    content: "Вы уверены, что титульный лист проверен?",
    isEditTest: false,
    okTest: "Подтвердить",
    cancelText: "Отмена",
  },
  REJECT: {
    title: "Отклонить титульный лист",
    content: "Вы уверены, что хотите отклонить титульный лист?",
    isEditTest: true,
    okTest: "Отклонить",
    cancelText: "Отмена",
  },
  RETURN_TO_WORK: {
    title: "Вернуть в работу",
    content: "Вы уверены, что хотите вернуть титульный лист в работу?",
    isEditTest: false,
    okTest: "Вернуть",
    cancelText: "Отмена",
  },
  RESEND: {
    title: "Повторная отправка",
    content: "Вы уверены, что хотите отправить титульный лист снова?",
    isEditTest: false,
    okTest: "Отправить",
    cancelText: "Отмена",
  },
  PREPARE_FILES_RQ: {
    title: "Повторная отправка",
    content: "Вы уверены, что хотите отправить титульный лист снова?",
    isEditTest: false,
    okTest: "Отправить",
    cancelText: "Отмена",
  },
  SIGN: {
    title: "Подписание титульного листа",
    content: "Вы уверены, что хотите подписать титульный лист?",
    isEditTest: false,
    okTest: "Подписать",
    cancelText: "Отмена",
  },
  DECLINE_SIGNATURE: {
    title: "Отклонение подписи",
    content: "Вы уверены, что хотите отклонить подписание титульного листа?",
    isEditTest: false,
    okTest: "Отклонить",
    cancelText: "Отмена",
  },
  SEND_TO_GSN: {
    title: "Отправить в ГСН",
    content: "Вы уверены, что хотите отправить титульный лист в ГСН?",
    isEditTest: false,
    okTest: "Отправить",
    cancelText: "Отмена",
  },
  REJECT_GSN: {
    title: "Отклонение ГСН",
    content: "Вы уверены, что хотите отклонить титульный лист для ГСН?",
    isEditTest: true,
    okTest: "Отклонить",
    cancelText: "Отмена",
  },
};

type Status = keyof typeof ModalContentMap;

export const SideButtonConfig = ({
  onOpen,
  setModalContentNew,
  setStatus,
}: ISideButtonConfig) => {
  const { titleId } = useCoordinator();

  const { data: titleData } = useQuery(
    journalsQueries.getTitleJournal({ titleId }),
  );

  const status = titleData?.mainJournal?.titleJournal?.status;

  const handleJournalAction = (status: Status) => () => {
    onOpen();
    setStatus(status);
    setModalContentNew(ModalContentMap[status]);
  };

  switch (status) {
    case "NEW":
      return (
        <div className="button-container">
          <Can I="edit" a="title_journals">
            <Button
              type="primary"
              icon={<SaveIcon />}
              onClick={handleJournalAction("SAVE")}
              style={{ marginTop: "8px", height: "40px" }}
            >
              Сохранить
            </Button>
          </Can>
          <Can I="edit" a="title_journals">
            <Button
              icon={<ArrowSendIcon />}
              onClick={handleJournalAction("SEND_APPROVAL")}
              type="default"
              style={{
                marginTop: "8px",
                backgroundColor: "rgba(234, 238, 248, 1)",
                height: "40px",
              }}
            >
              Отправить на проверку
            </Button>
          </Can>
        </div>
      );

    case "UNDER_APPROVAL":
      return (
        <div className="button-container">
          <Can I="edit" a="title_journals">
            <Button
              type="primary"
              icon={<BackCheckIcon />}
              onClick={handleJournalAction("REVOKE")}
              style={{ marginTop: "8px", height: "40px" }}
            >
              Отозвать
            </Button>
          </Can>
          <Can I="approve" a="title_journals">
            <Button
              type="primary"
              icon={<DoneIcon />}
              onClick={handleJournalAction("APPROVE")}
              style={{ marginTop: "8px", height: "40px" }}
            >
              Проверено
            </Button>
          </Can>
          <Can I="approve" a="title_journals">
            <Button
              type="default"
              icon={<CloseIcon />}
              onClick={handleJournalAction("REJECT")}
              style={{ marginTop: "8px", height: "40px" }}
            >
              Отклонить
            </Button>
          </Can>
        </div>
      );

    case "CANCELED":
    case "REJECTED":
      return (
        <Can I="edit" a="title_journals">
          <Button
            type="primary"
            icon={<DoneIcon />}
            onClick={handleJournalAction("RETURN_TO_WORK")}
            style={{ marginTop: "8px", width: "100%", height: "40px" }}
          >
            Вернуть в работу
          </Button>
        </Can>
      );

    case "DOCUMENT_ERROR":
      return (
        <Can I="approve" a="title_journals">
          <Button
            type="primary"
            icon={<SendCheckIcon />}
            onClick={handleJournalAction("PREPARE_FILES_RQ")}
            style={{ marginTop: "8px", width: "100%", height: "40px" }}
          >
            Повторная отправка
          </Button>
        </Can>
      );

    case "AT_SIGNING":
      return <></>;

    case "SIGNED":
      return (
        <div className="button-container">
          {/* <Can I="integration" a="title_journals"> */}
          <div>
            <Button
              type="primary"
              icon={<SendCheckIcon />}
              onClick={handleJournalAction("SEND_TO_GSN")}
              style={{ marginTop: "8px", width: "100%", height: "40px" }}
            >
              Отправить в ГСН
            </Button>
          </div>

          {/* </Can> */}
          {/* <Can I="integration" a="title_journals"> */}
          <div>
            <Button
              type="default"
              icon={<CloseIcon />}
              onClick={handleJournalAction("REJECT_GSN")}
              style={{ marginTop: "8px", width: "100%", height: "40px" }}
            >
              Отклонить
            </Button>
          </div>
          {/* </Can> */}
        </div>
      );

    case "REGISTERED_WITH_GSN":
      return null;
    case "PREPARE_XML":
      return null;
    case "SENT_TO_GSN":
      return null;

    default:
      return null;
  }
};
